import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle, Rule, Meta, Display } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useMultipleApi } from '../lib/api';
import { formatLocalDate } from '../i18n';
import { EmptyJournal } from '../components/Illustrations';

/**
 * Journal - dziennik notatek z sezonu.
 *
 * Brak osobnego endpointu /api/journal - składamy wpisy z dwóch źródeł:
 *   1. notes z bedów (b.note z /api/plots/all-with-details)
 *   2. captions ze zdjęć (photo.caption z /api/gallery)
 *
 * Wpisy łączymy chronologicznie (od najnowszego), featured = najnowszy
 * z najdłuższym opisem. Layout editorial - hero amber + lista poniżej.
 *
 * Endpoints:
 *   /api/plots/all-with-details
 *   /api/gallery
 */

const MIN_NOTE_LENGTH = 4; // Skipuj puste/jednoznakowe notes

function buildJournalEntries(plots, photos) {
  const entries = [];

  // Notes z bedów
  if (Array.isArray(plots)) {
    plots.forEach(plot => {
      (plot.beds || []).forEach(bed => {
        if (bed.note && bed.note.trim().length >= MIN_NOTE_LENGTH) {
          // Wybierz najwcześniejszą datę: bed.created_at, bed.planted_date
          const date = bed.created_at || bed.planted_date || new Date().toISOString();
          entries.push({
            id: `bed-${bed.id}`,
            date,
            place: `${plot.name} · rząd ${bed.row_number || '—'}`,
            title: bed.plant_name
              ? `${bed.plant_name}${bed.plant_variety ? ` (${bed.plant_variety})` : ''}`
              : 'Notatka',
            body: bed.note.trim(),
            tags: [bed.plant_name, bed.plant_variety].filter(Boolean),
            kind: 'bed',
          });
        }
      });
    });
  }

  // Captions ze zdjęć (jeśli różne od plant_name)
  if (Array.isArray(photos)) {
    photos.forEach(photo => {
      if (photo.caption && photo.caption.trim().length >= MIN_NOTE_LENGTH) {
        entries.push({
          id: `photo-${photo.id}`,
          date: photo.taken_date || photo.created_at,
          place: photo.plot_name
            ? `${photo.plot_name}${photo.bed_plant_name ? ` · ${photo.bed_plant_name}` : ''}`
            : 'Galeria',
          title: photo.bed_plant_name || 'Zdjęcie',
          body: photo.caption.trim(),
          tags: [photo.bed_plant_name, photo.bed_plant_variety, photo.tag].filter(Boolean),
          kind: 'photo',
        });
      }
    });
  }

  // Sortuj malejąco (najnowsze pierwsze)
  entries.sort((a, b) => new Date(b.date) - new Date(a.date));
  return entries;
}

function pickFeatured(entries) {
  if (entries.length === 0) return null;
  // Najnowsze 5 + wybór najdłuższego opisu (najbardziej "editorial")
  const recent = entries.slice(0, 5);
  return recent.reduce((best, e) =>
    (e.body.length > best.body.length ? e : best), recent[0]);
}

// ── Hero entry (amber background, duża typografia) ──────────────
function FeaturedEntry({ entry }) {
  return (
    <div style={{
      padding: '24px',
      borderRadius: 18,
      background: 'var(--t-amber)',
      color: 'var(--t-bg)',
    }}>
      <div style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.2em', textTransform: 'uppercase',
        opacity: 0.7, marginBottom: 10,
      }}>
        {formatLocalDate(entry.date, 'long')} · {entry.place}
      </div>

      <div style={{
        fontFamily: 'var(--t-display)', fontWeight: 300,
        fontSize: 'clamp(24px, 5vw, 32px)', lineHeight: 1.05,
        letterSpacing: '-.02em', marginBottom: 14,
        textWrap: 'balance',
      }}>{entry.title}</div>

      <div style={{
        fontSize: 14, lineHeight: 1.55,
        opacity: 0.85, marginBottom: 14,
      }}>{entry.body}</div>

      {entry.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {entry.tags.slice(0, 5).map((tag, i) => (
            <span key={i} style={{
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.16em', textTransform: 'uppercase',
              padding: '4px 9px', borderRadius: 999,
              background: 'rgba(15, 20, 16, 0.12)',
            }}>{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── List entry (zwykły wpis) ─────────────────────────────────────
function Entry({ entry }) {
  const day = entry.date ? new Date(entry.date).getDate() : '';
  const month = entry.date ? String(new Date(entry.date).getMonth() + 1).padStart(2, '0') : '';

  return (
    <div style={{
      display: 'flex', gap: 14,
      paddingBottom: 16,
      borderBottom: '1px solid var(--t-line)',
    }}>
      <div style={{ width: 48, flexShrink: 0, textAlign: 'right' }}>
        <Display size={20} style={{ display: 'block' }}>{day}</Display>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.14em', color: 'var(--t-text-mute)', marginTop: 2,
        }}>.{month}</div>
      </div>
      <div style={{ width: 1, background: 'var(--t-line-2)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Meta style={{ marginBottom: 4 }}>{entry.place}</Meta>
        <Display size={18} style={{
          display: 'block', lineHeight: 1.15, marginBottom: 6,
        }}>{entry.title}</Display>
        <div style={{
          fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5,
        }}>{entry.body}</div>
      </div>
    </div>
  );
}

// ── Main content ────────────────────────────────────────────────
function MainContent({ entries, mobile }) {
  const { t } = useTranslation();
  const featured = pickFeatured(entries);
  const rest = featured ? entries.filter(e => e.id !== featured.id) : entries;

  if (entries.length === 0) {
    return (
      <EmptyState
        illustration="journal"
        title="Dziennik czeka na pierwszą obserwację"
        description="Każda notatka, każde zdjęcie — to listek w Twojej kolekcji. Dodaj pierwszy wpis."
      />
    );
  }

  return (
    <>
      {featured && (
        <div style={{ margin: mobile ? '0 24px 22px' : '0 0 32px' }}>
          <FeaturedEntry entry={featured} />
        </div>
      )}

      {rest.length > 0 && (
        <>
          <Rule label="Wcześniej" />
          <div style={{
            padding: mobile ? '0 24px' : 0,
            display: 'flex', flexDirection: 'column', gap: 18,
          }}>
            {rest.map(e => <Entry key={e.id} entry={e} />)}
          </div>
        </>
      )}
    </>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ entries }) {
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>Dziennik · {entries.length} {entries.length === 1 ? 'wpis' : entries.length < 5 ? 'wpisy' : 'wpisów'}</Meta>
        <button
          aria-label="Nowy wpis"
          style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'var(--t-sage)', color: 'var(--t-bg)',
            border: 'none', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 3v10M3 8h10" stroke="var(--t-bg)" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <PageTitle title="Notatki z sezonu" eyebrow="Dziennik" />
      <MainContent entries={entries} mobile />
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ entries }) {
  const featured = pickFeatured(entries);
  const rest = featured ? entries.filter(e => e.id !== featured.id) : entries;

  return (
    <div className="t-stagger" style={{
      padding: '32px 40px 60px',
      display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 48, alignItems: 'start',
    }}>
      <div>
        <Meta style={{ marginBottom: 6 }}>
          Dziennik · {entries.length} {entries.length === 1 ? 'wpis' : entries.length < 5 ? 'wpisy' : 'wpisów'}
        </Meta>
        <Display size={48} style={{
          display: 'block', marginBottom: 28, letterSpacing: '-.03em',
        }}>Notatki z sezonu</Display>

        {entries.length === 0 ? (
          <EmptyState
            illustration="journal"
            title="Dziennik czeka na pierwszą obserwację"
            description="Każda notatka, każde zdjęcie — to listek w Twojej kolekcji."
          />
        ) : (
          featured && <FeaturedEntry entry={featured} />
        )}
      </div>

      <div>
        {rest.length > 0 && (
          <>
            <Meta style={{
              marginBottom: 16, paddingBottom: 8,
              borderBottom: '1px solid var(--t-line-2)',
            }}>Wcześniej</Meta>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {rest.map(e => <Entry key={e.id} entry={e} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Journal screen root ──────────────────────────────────────────
export default function Journal() {
  const { t } = useTranslation();
  const { isPhone } = useViewport();

  const { results, loading, errors } = useMultipleApi({
    plots: '/api/plots/all-with-details',
    gallery: '/api/gallery',
  });

  if (errors.plots && (errors.plots.includes('autoryz') || errors.plots === 'Brak tokenu autoryzacji')) {
    return <Layout active="journal"><ErrorState error={{ status: 401, message: errors.plots }} /></Layout>;
  }
  if (loading) return <Layout active="journal"><LoadingState label={t('common.loading')} /></Layout>;

  const entries = buildJournalEntries(results.plots, results.gallery);

  if (isPhone) return <Layout active="journal"><MobileContent entries={entries} /></Layout>;

  return (
    <Layout active="journal" topBar={<TopBar
      left={<Meta>Dziennik sezonu {new Date().getFullYear()}</Meta>}
      right={<Meta color="sage">+ Nowy wpis</Meta>}
    />}>
      <DesktopContent entries={entries} />
    </Layout>
  );
}
